import { CalendarDays } from 'lucide-react';
import { type FieldValues, useController } from 'react-hook-form';
import { type CalendarDate, parseAbsoluteToLocal, parseDate, type ZonedDateTime } from '@internationalized/date';
import { NumberField, NumberFieldGroup } from '../tailgrids/core/number-field.js';
import { Input } from '../tailgrids/core/input.js';
import {
  Calendar,
  CalendarCell,
  CalendarGrid,
  CalendarGridBody,
  CalendarGridHeader,
  CalendarHeader,
  CalendarHeading,
  NavButton,
} from '../tailgrids/core/calendar.js';
import { DateInput, DateSegment } from '../tailgrids/core/date-field.js';
import { DatePicker, DatePickerGroup, DatePickerPopover, DatePickerTrigger } from '../tailgrids/core/date-picker.js';
import { cn } from '../../utils/cn.js';
import { type EmptyAs, FormDescription, FormFieldError, type FormFieldBaseProps, FormLabel } from './field-parts.js';

export interface NumberFormFieldProps<TValues extends FieldValues = FieldValues> extends FormFieldBaseProps<TValues> {
  placeholder?: string;
  minValue?: number;
  maxValue?: number;
  step?: number;
  /** Number formatting, e.g. `{ style: 'percent' }` or `{ maximumFractionDigits: 2 }`. */
  formatOptions?: Intl.NumberFormatOptions;
  emptyAs?: EmptyAs;
}

/**
 * A number. The form holds a number (never the typed text); clearing the input writes `emptyAs`.
 * Bounds come from the schema; `minValue`/`maxValue` only steer the stepper and keyboard.
 */
export function NumberFormField<TValues extends FieldValues = FieldValues>({
  name,
  label,
  description,
  help,
  required,
  disabled,
  className,
  placeholder,
  minValue,
  maxValue,
  step,
  formatOptions,
  emptyAs,
}: NumberFormFieldProps<TValues>) {
  const { field, fieldState } = useController<TValues>({ name });
  const value = typeof field.value === 'number' ? field.value : Number.NaN;
  return (
    <NumberField
      className={cn('flex w-full flex-col gap-1.5', className)}
      name={field.name}
      value={value}
      onChange={(next) => field.onChange(Number.isNaN(next) ? emptyAs : next)}
      onBlur={field.onBlur}
      minValue={minValue}
      maxValue={maxValue}
      step={step}
      formatOptions={formatOptions}
      invalid={fieldState.invalid}
      required={required}
      disabled={disabled || field.disabled}
      validationBehavior="aria"
    >
      <FormLabel label={label} required={required} help={help} />
      <NumberFieldGroup className="w-full">
        <Input ref={field.ref} placeholder={placeholder} className="w-full px-3 py-2.5 text-sm" />
      </NumberFieldGroup>
      <FormDescription>{description}</FormDescription>
      <FormFieldError message={fieldState.error?.message} />
    </NumberField>
  );
}

export interface DateFormFieldProps<TValues extends FieldValues = FieldValues> extends FormFieldBaseProps<TValues> {
  /**
   * `day` holds an ISO date (`2026-10-16`); `minute` holds an ISO instant in UTC
   * (`2026-10-16T09:30:00.000Z`) entered in the person's local time.
   */
  granularity?: 'day' | 'minute';
  emptyAs?: EmptyAs;
}

function toDateValue(value: unknown, granularity: 'day' | 'minute'): CalendarDate | ZonedDateTime | null {
  if (typeof value !== 'string' || value === '') return null;
  try {
    return granularity === 'day' ? parseDate(value.slice(0, 10)) : parseAbsoluteToLocal(value);
  } catch {
    return null;
  }
}

export function DateFormField<TValues extends FieldValues = FieldValues>({
  name,
  label,
  description,
  help,
  required,
  disabled,
  className,
  granularity = 'day',
  emptyAs,
}: DateFormFieldProps<TValues>) {
  const { field, fieldState } = useController<TValues>({ name });
  return (
    <DatePicker<CalendarDate | ZonedDateTime>
      className={cn('gap-1.5', className)}
      name={field.name}
      value={toDateValue(field.value, granularity)}
      onChange={(next) => {
        if (next === null) field.onChange(emptyAs);
        else field.onChange('timeZone' in next ? next.toAbsoluteString() : next.toString());
      }}
      onBlur={field.onBlur}
      granularity={granularity}
      invalid={fieldState.invalid}
      required={required}
      disabled={disabled || field.disabled}
      validationBehavior="aria"
    >
      <FormLabel label={label} required={required} help={help} />
      <DatePickerGroup className="relative w-full">
        <DateInput className="w-full pr-11">{(segment) => <DateSegment segment={segment} />}</DateInput>
        <DatePickerTrigger aria-label="Open calendar" className="text-text-secondary hover:text-text-primary">
          <CalendarDays size={18} />
        </DatePickerTrigger>
      </DatePickerGroup>
      <FormDescription>{description}</FormDescription>
      <FormFieldError message={fieldState.error?.message} />
      <DatePickerPopover>
        <Calendar>
          <CalendarHeader>
            <NavButton slot="previous" />
            <CalendarHeading />
            <NavButton slot="next" />
          </CalendarHeader>
          <CalendarGrid>
            <CalendarGridHeader />
            <CalendarGridBody>{(date) => <CalendarCell date={date} />}</CalendarGridBody>
          </CalendarGrid>
        </Calendar>
      </DatePickerPopover>
    </DatePicker>
  );
}
